import { useCallback, useState } from "react";
import fileDownload from "js-file-download";
import axios from "axios";

export type GetFileAsync = (
  fileId: string,
) => Promise<{ fileName: string; fileUrl: string | undefined; fileExt: string | undefined }>;

interface UseFileDownloadProps {
  getFileAsync: GetFileAsync;
}

export interface OnDownloadActions {
  onSuccess?: () => void;
  onError?: () => void;
}

interface UseFileDownload {
  downloadFile: (fileId: string, onDownloadActions?: OnDownloadActions) => void;
  preparingDownload: boolean;
  downloadError: string;
}

const useFileDownload = ({ getFileAsync }: UseFileDownloadProps): UseFileDownload => {
  const [state, setState] = useState({
    preparingDownload: false,
    downloadError: "",
  });

  const downloadFile = useCallback(
    async (fileId: string, { onSuccess = () => {}, onError = () => {} }) => {
      try {
        setState({ preparingDownload: true, downloadError: "" });
        const { fileName, fileUrl, fileExt } = await getFileAsync(fileId);

        if (fileUrl) {
          const res = await axios.get(fileUrl, {
            responseType: "blob",
          });

          await fileDownload(res.data, `${fileName}.${fileExt}`);
        } else {
          throw new Error("Invalid file type");
        }
        setState((prev) => ({ ...prev, preparingDownload: false }));
        onSuccess();
      } catch (ex: any) {
        setState({ preparingDownload: false, downloadError: ex.message });
        onError();
      }
    },
    [getFileAsync],
  );

  return {
    ...state,
    downloadFile,
  };
};

export default useFileDownload;
